import React from 'react';
import { Printer, BookOpen } from 'lucide-react';
import OkiruLogo from './OkiruLogo';
import './PreReadingLetterhead.css';

const CAT_COLOR = { 'B-BBEE': '#06CDE1', 'ESG': '#10B981', 'AI Tools': '#BA0DA7', 'AI & Compliance': '#FF7512', 'Microsoft 365': '#0078d4', 'Developer Tools': '#58a6ff', 'Cybersecurity': '#dc2626' };

function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, j) => (p.startsWith('**') ? <strong key={j}>{p.slice(2, -2)}</strong> : p));
}

function renderBody(text) {
  if (!text) return null;
  return text.split('\n\n').filter(p => p.trim()).map((para, i) => {
    const lines = para.split('\n');
    const items = lines.filter(l => l.trimStart().startsWith('- '));
    if (items.length === lines.length) {
      return (
        <ul key={i} className="prl-list">
          {items.map((item, k) => <li key={k}>{renderInline(item.trimStart().slice(2))}</li>)}
        </ul>
      );
    }
    return (
      <p key={i}>
        {lines.map((line, j) => (
          <React.Fragment key={j}>
            {renderInline(line)}
            {j < lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </p>
    );
  });
}

function fmtDate(d) {
  return new Date(d).toLocaleDateString('en-ZA', { day: '2-digit', month: 'long', year: 'numeric' });
}

// Renders the "0. Required Pre-reading" lesson as a branded, printable Okiru
// document: masthead, meta line, the lesson's own sections in reading order,
// and a sourced footer. Built to stand alone as a hand-out or a video script
// reference, independent of the app chrome around it.
export default function PreReadingLetterhead({ course, lesson }) {
  const color = CAT_COLOR[course.category] || course.color || '#06CDE1';
  const sections = lesson.sections || [];
  const bySlug = id => sections.find(s => s.id === id);

  const checklist = bySlug('s1');
  const context = bySlug('s2');
  const caseBrief = bySlug('s3');
  const lessons = bySlug('s4');
  const questions = bySlug('s5');
  const coreIdea = sections.find(s => s.type === 'tip');
  const sources = lesson.sources && lesson.sources.length ? lesson.sources : null;
  const prepNote = bySlug('s8');

  return (
    <div className="prl-doc" style={{ borderTopColor: color }}>
      <div className="prl-actions no-print">
        <button className="btn btn-ghost" onClick={() => window.print()}>
          <Printer size={16} strokeWidth={1.75} /> Print / Download Brief
        </button>
      </div>

      <header className="prl-masthead">
        <OkiruLogo height={30} surface="light" />
        <div className="prl-masthead-right">
          <span className="prl-masthead-label">Pre-Reading Brief</span>
          <span className="prl-masthead-tag" style={{ color, borderColor: color + '40', background: color + '0D' }}>{course.category}</span>
        </div>
      </header>

      <div className="prl-title-block">
        <h1 className="prl-title">{lesson.title}</h1>
        <p className="prl-meta">
          Prepared for <strong>{course.title}</strong> &middot; {lesson.duration} read &middot; {fmtDate(new Date())}
        </p>
      </div>

      {checklist && (
        <div className="prl-checklist">
          <span className="prl-checklist-icon"><BookOpen size={16} strokeWidth={1.75} /></span>
          <div>
            <p className="prl-checklist-title">Before the course, you will</p>
            {renderBody(checklist.content)}
          </div>
        </div>
      )}

      {context && (
        <section className="prl-section">
          <h2>{context.title}</h2>
          {renderBody(context.content)}
        </section>
      )}

      {caseBrief && (
        <section className="prl-section prl-case">
          <h2>{caseBrief.title}</h2>
          {renderBody(caseBrief.content)}
        </section>
      )}

      {lessons && (
        <section className="prl-section">
          <h2>{lessons.title}</h2>
          {renderBody(lessons.content)}
        </section>
      )}

      {questions && (
        <section className="prl-section">
          <h2>{questions.title}</h2>
          {renderBody(questions.content)}
        </section>
      )}

      {coreIdea && (
        <div className="prl-core-idea" style={{ borderColor: color + '40', background: color + '0D' }}>
          {coreIdea.content}
        </div>
      )}

      {prepNote && (
        <section className="prl-section prl-prep-note">
          <h2>{prepNote.title}</h2>
          {renderBody(prepNote.content)}
        </section>
      )}

      {sources && (
        <footer className="prl-sources">
          <h3>Sources &amp; Further Reading</h3>
          <ul>
            {sources.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
          <p className="prl-disclaimer">
            This brief is original material prepared by Okiru Learn for training purposes. It is not a reproduction,
            summary or derivative of any third-party case study or licensed teaching case.
          </p>
        </footer>
      )}
    </div>
  );
}
